import React, { useState, useEffect } from 'react';
import {
  Users,
  Upload,
  Trash2,
  CheckCircle2,
  Linkedin,
  Image as ImageIcon,
  X,
  Edit2
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

// UPDATE: Added exact database matching names
const CATEGORY_OPTIONS = [
  'Board of Directors',
  'Governing Members',
  'Executive Team',
  'Management Team',
  'Volunteer Team'
];

const CUSTOM_CATEGORY_VALUE = '__custom_category__';

const getCategorySelectValue = (category: string) => {
  return CATEGORY_OPTIONS.includes(category) ? category : CUSTOM_CATEGORY_VALUE;
};

const ManageTeam = () => {
  const [members, setMembers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UPDATE: Default category set to 'Board of Directors'
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    bio: '',
    category: 'Board of Directors',
    linkedin_url: ''
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const [editModal, setEditModal] = useState({
    isOpen: false,
    data: {
      id: '',
      name: '',
      designation: '',
      bio: '',
      category: '',
      linkedin_url: '',
      profile_image: ''
    }
  });

  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editPreview, setEditPreview] = useState<string | null>(null);

  const backendUrl = "https://www.sevaafoundation.com/seva-backend";
  const token = localStorage.getItem("sevaa_admin_token");

 const fetchMembers = async () => {
  try {
    // Timestamp add karne se browser hamesha fresh data layega
    const res = await fetch(`${backendUrl}/api/team/list_members.php?t=${new Date().getTime()}`);
    const data = await res.json();
    if (data.success) {
      setMembers(data.members);
    }
  } catch (error) {
    toast.error("Failed to load team");
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isEdit: boolean
  ) => {
    if (e.target.files && e.target.files[0]) {
      if (isEdit) {
        setEditImageFile(e.target.files[0]);
        setEditPreview(URL.createObjectURL(e.target.files[0]));
      } else {
        setImageFile(e.target.files[0]);
        setPreview(URL.createObjectURL(e.target.files[0]));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.category.trim()) {
      return toast.error("Please select or enter category");
    }

    if (!imageFile) {
      return toast.error("Please upload a profile photo");
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Adding member...");

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("designation", formData.designation);
    submitData.append("bio", formData.bio);
    submitData.append("category", formData.category.trim());
    submitData.append("linkedin_url", formData.linkedin_url);
    submitData.append("profile_image", imageFile);

    try {
      const response = await fetch(`${backendUrl}/api/team/add_member.php`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Member added successfully!", { id: loadingToast });
        setFormData({
          name: '',
          designation: '',
          bio: '',
          category: 'Board of Directors',
          linkedin_url: ''
        });
        setImageFile(null);
        setPreview(null);
        fetchMembers();
      } else {
        toast.error(data.error || "Failed to add", { id: loadingToast });
      }
    } catch (err) {
      toast.error("Server Error", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editModal.data.category.trim()) {
      return toast.error("Please select or enter category");
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Updating member...");

    const submitData = new FormData();
    submitData.append("id", editModal.data.id);
    submitData.append("name", editModal.data.name);
    submitData.append("designation", editModal.data.designation);
    submitData.append("bio", editModal.data.bio);
    submitData.append("category", editModal.data.category.trim());
    submitData.append("linkedin_url", editModal.data.linkedin_url);

    if (editImageFile) {
      submitData.append("profile_image", editImageFile);
    }

    try {
      const response = await fetch(`${backendUrl}/api/team/edit_member.php`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Member updated successfully!", { id: loadingToast });
        setEditModal({
          isOpen: false,
          data: { id: '', name: '', designation: '', bio: '', category: '', linkedin_url: '', profile_image: '' }
        });
        setEditImageFile(null);
        setEditPreview(null);
        fetchMembers();
      } else {
        toast.error(data.error || "Failed to update", { id: loadingToast });
      }
    } catch (err) {
      toast.error("Server Error", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      const response = await fetch(`${backendUrl}/api/team/delete_member.php?id=${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Member removed");
        setMembers(members.filter(m => m.id !== id));
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Deletion failed");
    }
  };

  const openEditModal = (member: any) => {
    setEditModal({ isOpen: true, data: { ...member } });
    setEditPreview(`${backendUrl}${member.profile_image}`);
  };

  const addCategorySelectValue = getCategorySelectValue(formData.category);
  const editCategorySelectValue = getCategorySelectValue(editModal.data.category);

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <Toaster position="top-right" />

      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
          <Users className="text-orange-500" /> Manage Team
        </h1>
        <p className="text-slate-500 mt-1">Add or remove members from your organization's hierarchy.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ADD MEMBER FORM */}
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-fit">
          <h2 className="font-bold text-lg mb-4 text-slate-800 border-b pb-2">Add New Member</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
              <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Designation</label>
              <input required type="text" value={formData.designation} onChange={e => setFormData({ ...formData, designation: e.target.value })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="e.g. Founder" />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Biography</label>
              <textarea value={formData.bio} onChange={e => setFormData({ ...formData, bio: e.target.value })} rows={6} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 custom-scrollbar" placeholder="Enter full detailed biography here... (No word limit)" />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
              <select
                value={addCategorySelectValue}
                onChange={e => {
                  const value = e.target.value;
                  setFormData({ ...formData, category: value === CUSTOM_CATEGORY_VALUE ? '' : value });
                }}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500"
              >
                {CATEGORY_OPTIONS.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
                <option value={CUSTOM_CATEGORY_VALUE}>Other / Manual Type</option>
              </select>

              {addCategorySelectValue === CUSTOM_CATEGORY_VALUE && (
                <input required type="text" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="mt-3 w-full px-4 py-2.5 bg-slate-50 border border-orange-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="Enter custom category, e.g. District Team" />
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">LinkedIn URL (Optional)</label>
              <input type="url" value={formData.linkedin_url} onChange={e => setFormData({ ...formData, linkedin_url: e.target.value })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Profile Photo</label>
              <div className="relative border-2 border-dashed border-slate-200 bg-slate-50 hover:border-orange-400 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer">
                {preview ? <img src={preview} alt="Preview" className="w-20 h-20 object-cover rounded-full shadow-md" /> : <ImageIcon className="text-slate-400" size={32} />}
                <p className="text-xs text-slate-500 mt-2 font-medium">{preview ? 'Change Photo' : 'Upload Photo'}</p>
                <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, false)} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition-all flex justify-center gap-2">
              <CheckCircle2 size={20} /> {isSubmitting ? 'Adding...' : 'Add Member'}
            </button>
          </form>
        </div>

        {/* TEAM LIST */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="p-8 text-center text-slate-500">Loading members...</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 relative">
              {members.length === 0 && <p className="text-slate-500 col-span-full bg-white p-8 rounded-2xl text-center border">No team members added yet.</p>}
              {members.map(member => (
                <div key={member.id} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4 relative group hover:shadow-md transition-shadow">
                  <button onClick={() => handleDelete(member.id)} className="absolute top-2 right-2 p-1.5 bg-red-50 text-red-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white z-10"><Trash2 size={16} /></button>
                  <button onClick={() => openEditModal(member)} className="absolute bottom-2 right-2 p-1.5 bg-blue-50 text-blue-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600 hover:text-white z-10"><Edit2 size={16} /></button>
                  <img src={`${backendUrl}${member.profile_image}`} alt={member.name} className="w-16 h-16 object-cover rounded-full border-2 border-slate-100" />
                  <div>
                    <h4 className="font-bold text-slate-800">{member.name}</h4>
                    <p className="text-xs text-orange-600 font-semibold">{member.designation}</p>
                    <span className="inline-block mt-1 bg-slate-100 text-slate-500 text-[10px] uppercase font-bold px-2 py-0.5 rounded">{member.category}</span>
                    {member.linkedin_url && <a href={member.linkedin_url} target="_blank" rel="noreferrer" className="mt-2 text-blue-500 flex items-center gap-1 text-xs hover:underline"><Linkedin size={12} /> LinkedIn</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* EDIT MODAL */}
      {editModal.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-xl p-8 relative shadow-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button onClick={() => setEditModal({ isOpen: false, data: { id: '', name: '', designation: '', bio: '', category: '', linkedin_url: '', profile_image: '' } })} className="absolute top-6 right-6 p-2 bg-slate-100 text-slate-500 hover:bg-slate-900 hover:text-white rounded-full transition-colors"><X size={20} /></button>
            <h2 className="font-bold text-2xl mb-6 text-slate-800 flex items-center gap-3"><Edit2 className="text-blue-600" /> Edit Member Details</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
                  <input required type="text" value={editModal.data.name} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, name: e.target.value } })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Designation</label>
                  <input required type="text" value={editModal.data.designation} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, designation: e.target.value } })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Biography</label>
                <textarea value={editModal.data.bio} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, bio: e.target.value } })} rows={8} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 custom-scrollbar" placeholder="Enter full detailed biography here... (No word limit)" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                  <select
                    value={editCategorySelectValue}
                    onChange={e => {
                      const value = e.target.value;
                      setEditModal({ ...editModal, data: { ...editModal.data, category: value === CUSTOM_CATEGORY_VALUE ? '' : value } });
                    }}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORY_OPTIONS.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value={CUSTOM_CATEGORY_VALUE}>Other / Manual Type</option>
                  </select>

                  {editCategorySelectValue === CUSTOM_CATEGORY_VALUE && (
                    <input required type="text" value={editModal.data.category} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, category: e.target.value } })} className="mt-3 w-full px-4 py-2.5 bg-slate-50 border border-blue-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter custom category" />
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">LinkedIn URL</label>
                  <input type="url" value={editModal.data.linkedin_url} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, linkedin_url: e.target.value } })} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Profile Photo (Leave blank to keep same)</label>
                <div className="relative border-2 border-dashed border-slate-200 bg-slate-50 hover:border-blue-400 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer">
                  {editPreview ? <img src={editPreview} alt="Preview" className="w-16 h-16 object-cover rounded-full shadow-md" /> : <ImageIcon className="text-slate-400" size={32} />}
                  <p className="text-xs text-slate-500 mt-2 font-medium">Click to change photo</p>
                  <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, true)} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-2xl transition-all flex justify-center gap-2">
                <CheckCircle2 size={20} /> {isSubmitting ? 'Updating...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeam;